import { IFile } from './box'

export interface PastPrecedents {
  pastDeal: string
  changes: string
  section: string
  attorney: string
  // Label ([[P1]]) of the retrieved precedent row this entry came from. Present
  // only when the assistant cited a row; the row's own values then replace the
  // model's for every field below, so a link is never built from invented text.
  precedentRef?: string
  lawFirm?: string
  finalSection?: string
  initialText?: string
  finalText?: string
  // Resolved from the deal's executed LSA when the redlines are read; never persisted.
  documentLink?: string | null
  documentName?: string | null
}

export enum RedlineType {
  INSERTION = 'insertion',
  DELETION = 'deletion',
  REPLACEMENT = 'replacement',
}

/**
 * What kind of edit a redline makes, assigned by the redline assistant at
 * prediction time so similar edits can be grouped for bulk accept/reject.
 *
 * HIGH_JUDGMENT is a bucket, not a gate: it keeps negotiated terms of art
 * ("material adverse change", discretion over remedies) out of the boilerplate
 * categories and sorts them last, so a reviewer meets them deliberately rather
 * than sweeping them up with a batch of day-count edits. The group itself
 * resolves like any other - select-all and bulk accept/reject apply.
 */
export enum RedlineChangeCategory {
  NOTICE_PERIOD = 'notice_period',
  REASONABLENESS_QUALIFIER = 'reasonableness_qualifier',
  MONETARY_THRESHOLD = 'monetary_threshold',
  COST_ALLOCATION = 'cost_allocation',
  HIGH_JUDGMENT = 'high_judgment',
  // Assigned locally to redlines the materiality pre-check auto-resolves — those
  // never reach the assistant.
  FORMATTING = 'formatting',
  OTHER = 'other',
}

export const REDLINE_CHANGE_CATEGORY_LABELS: Record<RedlineChangeCategory, string> = {
  [RedlineChangeCategory.NOTICE_PERIOD]: 'Notice periods & day counts',
  [RedlineChangeCategory.REASONABLENESS_QUALIFIER]: 'Reasonableness qualifiers',
  [RedlineChangeCategory.MONETARY_THRESHOLD]: 'Monetary thresholds',
  [RedlineChangeCategory.COST_ALLOCATION]: 'Cost allocation',
  [RedlineChangeCategory.HIGH_JUDGMENT]: 'Negotiated terms',
  [RedlineChangeCategory.FORMATTING]: 'Formatting only',
  [RedlineChangeCategory.OTHER]: 'Other changes',
}

export const REDLINE_TYPE_LABELS = {
  [RedlineType.INSERTION]: 'Addition',
  [RedlineType.DELETION]: 'Deletion',
  [RedlineType.REPLACEMENT]: 'Replacement',
}

export interface RedlineSection {
  id?: string
  type?: RedlineType
  content: string
  originalContent?: string
  location: {
    paragraph: string
    beforeText: string
    afterText: string
    position?: number
  }
  metadata?: {
    author?: string
    date?: string
    replacedText?: string
  }
  suggestedAction?: SuggestedAction
  suggestedText?: string
  recommendation?: string
  reasoning?: string
  pastPrecedents?: PastPrecedents[]
  xmlId?: string
  section?: string
  processed?: boolean
  resolution?: SuggestedAction
  resolutionContent?: string
  comment?: string
  // True when the change is whitespace/punctuation/casing-only and was auto-resolved
  // at prediction time (no manual accept/reject required).
  isImmaterial?: boolean
  // Null on redlines predicted before the column existed; those group under OTHER.
  changeCategory?: RedlineChangeCategory | null
}

export interface RedlineData {
  data: RedlineSection[]
  total: number
  // Resolved redlines across the whole file, not just the loaded page.
  resolvedTotal?: number
  // Section numbers that still have at least one unresolved redline, so the
  // sections sidebar can mark remaining work rather than any-changes-at-all.
  unresolvedSections?: string[]
  predictionProgress?: {
    // Frames are broadcast to every UW user, so the panel shows only its own file's.
    fileId?: string
    current: number
    total: number
    // Set when AI suggestions stopped early; shown in place of the progress bar.
    error?: string
  }
}

export interface ParsedDocument {
  buffer: {
    data: Buffer
    type: string
  } // Original document buffer for docx-preview
  redlines?: RedlineSection[]
  sections?: { number: string; text: string }[]
  metadata: {
    author?: string
    lastModifiedBy?: string
    created?: string
    modified?: string
  }
  file: IFile
}

export enum SuggestedAction {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  ACCEPT_WITH_AMENDMENTS = 'ACCEPT_WITH_AMENDMENTS',
  UNKNOWN = 'UNKNOWN',
}
